import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  createPrescription,
  createCompoundedPrescription,
  CreateCompoundedPrescriptionParams,
  CreatePrescriptionParams,
} from "@screens/FeaAuth/hooks";

interface Value {
  isCreatingPrescription: boolean;
  errorOnPrescription: unknown;
  triggerCreatePrescription: (
    params: CreatePrescriptionParams
  ) => Promise<void>;
  isCreatingCompoundedPrescription: boolean;
  errorOnCompoundedPrescription: unknown;
  triggerCreateCompoundedPrescription: (
    params: CreateCompoundedPrescriptionParams
  ) => Promise<void>;
}

const DocumentCreationContext = createContext<Value | null>(null);

export const useDocumentCreationContext = () => {
  const value = useContext(DocumentCreationContext);
  if (!value) {
    throw new Error("DocumentCreationContext: No value provided");
  }
  return value;
};

export const DocumentCreationContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [isCreatingPrescription, setIsCreatingPrescription] = useState(false);
  const [errorOnPrescription, setErrorOnPrescription] = useState<unknown>(null);

  const triggerCreatePrescription = useCallback(
    async (params: CreatePrescriptionParams) => {
      setIsCreatingPrescription(true);
      setErrorOnPrescription(null);
      try {
        await createPrescription(params);
      } catch (error) {
        console.error(error);
        setErrorOnPrescription(error);
        // Also to the caller: a simple prescription is created and signed in
        // this one call, so its screen must know it failed.
        throw error;
      } finally {
        setIsCreatingPrescription(false);
      }
    },
    []
  );

  const [
    isCreatingCompoundedPrescription,
    setIsCreatingCompoundedPrescription,
  ] = useState(false);
  const [errorOnCompoundedPrescription, setErrorOnCompoundedPrescription] =
    useState<unknown>(null);

  const triggerCreateCompoundedPrescription = useCallback(
    async (params: CreateCompoundedPrescriptionParams) => {
      setIsCreatingCompoundedPrescription(true);
      setErrorOnCompoundedPrescription(null);
      try {
        await createCompoundedPrescription(params);
      } catch (error) {
        console.error(error);
        setErrorOnCompoundedPrescription(error);
      } finally {
        setIsCreatingCompoundedPrescription(false);
      }
    },
    []
  );

  const value = useMemo(
    () => ({
      isCreatingPrescription,
      errorOnPrescription,
      triggerCreatePrescription,
      isCreatingCompoundedPrescription,
      errorOnCompoundedPrescription,
      triggerCreateCompoundedPrescription,
    }),
    [
      isCreatingPrescription,
      errorOnPrescription,
      triggerCreatePrescription,
      isCreatingCompoundedPrescription,
      errorOnCompoundedPrescription,
      triggerCreateCompoundedPrescription,
    ]
  );

  return (
    <DocumentCreationContext.Provider value={value}>
      {children}
    </DocumentCreationContext.Provider>
  );
};
